import React, { Suspense, lazy, useMemo } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'

import { FeatureFlagKeys } from '@hapana/shared/constants/feature-flag.constants'
import { useFeatureFlag } from '@hapana/shared/web/hooks/useFeatureFlag'

import { styles } from './loading.styles'
import { LOADING_TEST_ID } from './loading.constants'
import { LoaderManager } from './manager'
import { HapanaLoader } from './variants/hapana/hapana-loader.component'

// Modern ES6 imports - much cleaner!

/**
 * Inline Themed Loader Components
 *
 * These are simplified versions of the themed loaders that can be used
 * directly in the JavaScript loading component.
 */

// Christmas Loader Component - Enhanced with dramatic animations

// Snowflake Loader Component - Enhanced detailed crystalline structure

// Summer Loader Component

// Valentine's Day Loader Component - Floating Love Hearts

// New Year Loader Component - "HNY" Fireworks

// Fourth of July Loader Component - Red & Blue Fireworks

// Chinese New Year Loader Component - Fire Horse 2026

// Diwali Loader Component - Flickering Diyas

// Default Hapana Loader Component

/**
 * Contextual loading messages for different operations
 */
const CONTEXTUAL_MESSAGES = {
  corporates: {
    fetch: (count) => `Loading corporate data${count ? ` (${count} items)` : ''}...`,
    create: () => 'Creating corporate profile...',
    update: () => 'Updating corporate settings...',
    delete: () => 'Removing corporate data...',
    process: () => 'Processing corporate configurations...',
  },
  users: {
    fetch: (count) => `Loading users${count ? ` (${count} items)` : ''}...`,
    create: () => 'Creating user account...',
    update: () => 'Updating user profile...',
    delete: () => 'Removing user account...',
    authenticate: () => 'Authenticating user...',
    provision: () => 'Provisioning user access...',
  },
  sites: {
    fetch: (count) => `Loading gym locations${count ? ` (${count} items)` : ''}...`,
    create: () => 'Setting up new gym location...',
    update: () => 'Updating gym settings...',
    delete: () => 'Removing gym location...',
    configure: () => 'Applying gym configuration...',
  },
  packages: {
    fetch: (count) => `Loading membership packages${count ? ` (${count} items)` : ''}...`,
    create: () => 'Creating membership package...',
    update: () => 'Updating package details...',
    delete: () => 'Removing membership package...',
    calculate: () => 'Calculating package pricing...',
  },
  sessions: {
    fetch: (count) => `Loading class sessions${count ? ` (${count} items)` : ''}...`,
    create: () => 'Scheduling new class session...',
    update: () => 'Updating session details...',
    delete: () => 'Cancelling class session...',
    book: () => 'Processing class booking...',
  },
  reports: {
    fetch: () => 'Generating report data...',
    export: () => 'Preparing data export...',
    calculate: () => 'Computing analytics...',
    aggregate: () => 'Aggregating metrics...',
  },
  default: {
    fetch: (count) => `Loading data${count ? ` (${count} items)` : ''}...`,
    create: () => 'Creating new item...',
    update: () => 'Saving changes...',
    delete: () => 'Removing item...',
    process: () => 'Processing request...',
  },
}

const SEASONAL_LOADERS = {
  halloween: lazy(() =>
    import('./variants/halloween-new/halloween-loader.component').then((module) => ({
      default: module.HalloweenNewLoader,
    }))
  ),
  christmas: lazy(() =>
    import('./variants/christmas/christmas-loader.component').then((module) => ({
      default: module.ChristmasLoader,
    }))
  ),
  snowflake: lazy(() =>
    import('./variants/snowflake/snowflake-loader.component').then((module) => ({
      default: module.SnowflakeLoader,
    }))
  ),
  summer: lazy(() =>
    import('./variants/summer/summer-loader.component').then((module) => ({
      default: module.SummerLoader,
    }))
  ),
  valentines: lazy(() =>
    import('./variants/valentines/valentines-loader.component').then((module) => ({
      default: module.ValentinesLoader,
    }))
  ),
}

const selectSeasonalLoader = (loaderOverride) => {
  const variantName = loaderOverride || LoaderManager.getInstance().getLoaderVariant().name
  return SEASONAL_LOADERS[variantName] ?? HapanaLoader
}

/**
 * Generate contextual loading message
 */
const generateContextualMessage = (context, operation, itemCount) => {
  const contextMessages = CONTEXTUAL_MESSAGES[context] || CONTEXTUAL_MESSAGES.default
  const messageGenerator = contextMessages[operation] || contextMessages.process
  return typeof messageGenerator === 'function' ? messageGenerator(itemCount) : messageGenerator
}

/**
 * Hapana Loading Component with Dynamic Themed Loaders
 *
 * Displays contextual loading states with seasonal theme variations.
 * Automatically selects appropriate loader based on current date and manual overrides.
 */
const Loading = ({
  label,
  buttonLabel: _buttonLabel = false,
  forceFallback: _forceFallback = false,
  context,
  countryCode,
  brandId,
  region,
  loaderOverride,
  disableDynamicLoaders = false,
  operation,
  itemCount,
  size,
}) => {
  /**
   * Generate contextual loading props
   */
  const baseProps = useMemo(() => {
    // Generate contextual message if not provided
    const contextualLabel =
      label ||
      (context && operation ? generateContextualMessage(context, operation, itemCount) : null)

    return {
      size: size || 24,
      label: contextualLabel,
      context: context || 'default',
      countryCode,
      brandId,
      region,
    }
  }, [label, context, operation, itemCount, countryCode, brandId, region, size])

  /**
   * Get dynamic loader component
   */
  const { value: seasonalLoadersEnabled } = useFeatureFlag(FeatureFlagKeys.SeasonalLoadersEnabled)
  const showSeasonalLoader = seasonalLoadersEnabled === true && !disableDynamicLoaders

  const { LoaderComponent, loaderProps } = useMemo(() => {
    return {
      LoaderComponent: showSeasonalLoader ? selectSeasonalLoader(loaderOverride) : HapanaLoader,
      loaderProps: baseProps,
    }
  }, [baseProps, showSeasonalLoader, loaderOverride])

  if (LoaderComponent) {
    return (
      <Box
        className="hapana-loading dynamic-loader"
        data-testid={LOADING_TEST_ID}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 3,
          minHeight: 120,
        }}
      >
        <Suspense fallback={<HapanaLoader {...loaderProps} />}>
          <LoaderComponent {...loaderProps} />
        </Suspense>
      </Box>
    )
  }

  // Fallback to default CircularProgress loader
  return (
    <Box
      className="hapana-loading default-loader"
      data-testid={LOADING_TEST_ID}
      sx={{
        ...styles.loadingContainer,
        minHeight: 120,
        padding: 3,
      }}
    >
      <CircularProgress
        size={loaderProps.size}
        sx={{
          color: 'blue.dark',
          marginBottom: 2,
        }}
      />
      {loaderProps.label && (
        <Typography
          variant="body2"
          sx={{
            ...styles.loadingText,
            textAlign: 'center',
            maxWidth: 300,
          }}
        >
          {loaderProps.label}
        </Typography>
      )}
    </Box>
  )
}

export default Loading
